/**
 * Per-turn cost, from the provider's own token counts.
 *
 * Two different kinds of number live here and they are labelled as such. Anthropic
 * publishes its rates. OpenAI's gpt-5.x rates are NOT publicly fixed — the figures
 * below are Maven's own deliberately-high ceiling, lifted from
 * projects-files-service/scripts/catchup/config.ts, which documents them as a
 * cost ceiling rather than a price list. Treat anything marked `estimate` as an
 * upper bound.
 *
 * A model with no known rate reports null, never zero: "free" is a worse answer
 * than "unknown".
 */

export type Rate = { inPerM: number; outPerM: number; source: 'published' | 'estimate' };

const RATES: Record<string, Rate> = {
  // Anthropic — published
  'claude-opus-5': { inPerM: 5, outPerM: 25, source: 'published' },
  'claude-opus-4-8': { inPerM: 5, outPerM: 25, source: 'published' },
  'claude-opus-4-7': { inPerM: 5, outPerM: 25, source: 'published' },
  'claude-opus-4-6': { inPerM: 5, outPerM: 25, source: 'published' },
  'claude-sonnet-5': { inPerM: 2, outPerM: 10, source: 'published' },
  'claude-sonnet-4-6': { inPerM: 3, outPerM: 15, source: 'published' },
  'claude-haiku-4-5': { inPerM: 1, outPerM: 5, source: 'published' },
  'claude-fable-5': { inPerM: 10, outPerM: 50, source: 'published' },
  'claude-fable-5-1': { inPerM: 10, outPerM: 50, source: 'published' },

  // OpenAI — Maven's conservative ceiling, not a published price
  'gpt-5.2': { inPerM: 2.5, outPerM: 10, source: 'estimate' },
  'gpt-5.4': { inPerM: 2.5, outPerM: 10, source: 'estimate' },
  'gpt-5': { inPerM: 2.5, outPerM: 10, source: 'estimate' },
};

/** Strips a dated snapshot suffix: the worker reports gpt-5.2-2025-12-11, we rate gpt-5.2. */
function rollingId(modelId: string): string {
  return modelId.replace(/-(?:\d{4}-\d{2}-\d{2}|\d{8})$/, '');
}

export function rateFor(modelId: string | undefined | null): Rate | null {
  if (!modelId) return null;
  return RATES[modelId] ?? RATES[rollingId(modelId)] ?? null;
}

export type Cost = { usd: number; source: Rate['source'] };

export function costOf(
  modelId: string | undefined | null,
  inputTokens: number | undefined,
  outputTokens: number | undefined,
): Cost | null {
  const rate = rateFor(modelId);
  if (!rate) return null;
  return {
    usd: ((inputTokens ?? 0) * rate.inPerM + (outputTokens ?? 0) * rate.outPerM) / 1_000_000,
    source: rate.source,
  };
}

/** Sub-cent amounts keep their significant digits — rounding them to $0.00 hides the number. */
export function formatUsd(usd: number): string {
  if (usd === 0) return '$0';
  if (usd < 0.001) return `$${usd.toFixed(5)}`;
  if (usd < 1) return `$${usd.toFixed(4)}`;
  return `$${usd.toFixed(2)}`;
}
